import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { FreeNumberInput } from "@/components/ui/free-number-input";
import {
  ChatsCircle, MagnifyingGlass, X, UserCircle, DeviceMobile, Desktop, Globe,
  ArrowSquareOut, Stack, BookOpenText, Sparkle, Clock, EnvelopeSimple, Heart, SpeakerHigh,
  CurrencyDollarSimple, Funnel, BookmarkSimple, CheckSquare, Square, Trash, CaretDown,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import SiloPickerModal from "@/components/SiloPickerModal";
import { useAnchoredScrollTarget } from "@/hooks/useAnchoredCollapse";

const MEMBER_BADGES = {
  anonymous:      { label: "Anonymous",     dropLabel: "Anonymous",        bg: "#F0F1EE", fg: "#6B6A68" },
  authenticated:  { label: "Signed-in",     dropLabel: "Signed-in",        bg: "#E8ECF1", fg: "#3A5A78" },
  subscriber:     { label: "Subscriber 💰", dropLabel: "Subscribers (paid)", bg: "#FFF3DD", fg: "#8A5A00" },
  creator:        { label: "Creator",       dropLabel: "Creators",         bg: "#E6F2E6", fg: "#2F6B3A" },
  super_admin:    { label: "Super Admin",   dropLabel: "Super Admin",      bg: "#F3E8FF", fg: "#5B21B6" },
};

// May 25, 2026 — canonical visitor-role badges for the new "Role"
// column. Sourced from `visitor_role` on each session (derived server-
// side via role_helpers.derive_role). Falls back to MEMBER_BADGES.
const VISITOR_ROLE_BADGES = {
  anonymous:          { label: "Anonymous",      bg: "#F0F1EE", fg: "#6B6A68" },
  fan:                { label: "Free follower",  bg: "#E8ECF1", fg: "#3A5A78" },
  paying:             { label: "Paying",         bg: "#FFF3DD", fg: "#8A5A00" },
  new_creator:        { label: "Creator",        bg: "#E6F2E6", fg: "#2F6B3A" },
  creator:            { label: "Creator",        bg: "#E6F2E6", fg: "#2F6B3A" },
  affiliate_creator:  { label: "Creator Pro",    bg: "#E6F1FF", fg: "#1E40AF" },
  super_admin:        { label: "Super admin",    bg: "#F3E8FF", fg: "#5B21B6" },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
};
const relativeTime = (iso) => {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};
const fmt$ = (cents) => (cents ? `$${(cents / 100).toFixed(2)}` : "—");

export default function ConversationsTab({ siloId, siloSlug }) {
  const nav = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [memberFilter, setMemberFilter] = useState("");
  const [minMessages, setMinMessages] = useState(0);
  const [search, setSearch] = useState("");
  const [ownerView, setOwnerView] = useState("all"); // Feb 17, 2026 — "all" | "hide_mine" | "only_mine"
  const [source, setSource] = useState("public"); // Jun 2026 (Randy) — "public" | "assistant"
  // Feb 2026 (Randy pt-yt-chevron) — chevron-collapsible for the Public
  // Q&A / AI Assistant section so YT Replies feels top-of-tab when
  // creator is focused on that workflow. Expanded by default so first-
  // time visitors don't miss the feed; localStorage remembers per silo.
  const conversationsCollapseKey = `mysilo_chats_conversations_open_${siloId || "default"}`;
  const readCollapse = (key) => {
    try {
      const stored = window.localStorage.getItem(key);
      return stored === null ? true : stored === "1";
    } catch { return true; }
  };
  const [conversationsOpen, setConversationsOpen] = useState(() => readCollapse(conversationsCollapseKey));
  // Sep 30, 2026 (Randy — "the chevron is hit or miss in remembering that I
  // minimized it"). This component is NOT remounted when you move between
  // /dashboard/silo/A and /dashboard/silo/B — same route pattern, so React
  // keeps the instance and only swaps the prop. The old single effect then
  // wrote the PREVIOUS silo's chevron state straight over the new silo's saved
  // choice, which is exactly why it remembered sometimes and not others. Now a
  // key change re-reads instead of writing.
  const lastCollapseKeyRef = useRef(conversationsCollapseKey);
  useEffect(() => {
    if (lastCollapseKeyRef.current !== conversationsCollapseKey) {
      lastCollapseKeyRef.current = conversationsCollapseKey;
      setConversationsOpen(readCollapse(conversationsCollapseKey));
      return;
    }
    try { window.localStorage.setItem(conversationsCollapseKey, conversationsOpen ? "1" : "0"); } catch { /* ignore */ }
  }, [conversationsOpen, conversationsCollapseKey]);
  // Iter160t (platform chevron sweep) — anchor-scroll so toggling this
  // section doesn't jump the page. The YT Replies section below is a
  // frequent target of user scrolling, so keeping their scroll position
  // stable when this collapses/expands is a real UX win.
  const { cardRef: conversationsRef, withAnchor: withConversationsAnchor } = useAnchoredScrollTarget();
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionDetail, setSessionDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [picker, setPicker] = useState({ open: false, mode: "commit", messages: [] });
  // Jun 2026 (Randy) — Select / multi-delete. Public Q&A: only your OWN self-test
  // chats (or saved bookmarks) are selectable; visitor conversations are exempt.
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const isSelectable = (s) => source === "assistant" || s.is_own || s.is_bookmark;
  const toggleSel = (id) => setSelectedIds((prev) => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  useEffect(() => { setSelectMode(false); setSelectedIds(new Set()); }, [source]);
  const deleteSelected = async () => {
    if (!selectedIds.size) return;
    const n = selectedIds.size;
    if (!window.confirm(`Delete ${n} selected conversation${n > 1 ? "s" : ""}? This can't be undone.`)) return;
    const base = source === "assistant" ? `/silos/${siloId}/assistant-sessions` : `/silos/${siloId}/public-sessions`;
    try {
      const r = await api.post(`${base}/bulk-delete`, { session_ids: Array.from(selectedIds) });
      toast.success(`Deleted ${r.data?.deleted ?? 0} conversation${(r.data?.deleted ?? 0) === 1 ? "" : "s"}.`);
      setSelectMode(false); setSelectedIds(new Set());
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not delete."); }
  };

  const load = useCallback(async () => {
    if (!siloId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (memberFilter) params.set("member_level", memberFilter);
      if (minMessages) params.set("min_messages", String(minMessages));
      if (search.trim()) params.set("q", search.trim());
      if (ownerView && ownerView !== "all") params.set("owner_view", ownerView);
      // May 25, 2026 — cap at 30 most recent per silo. Server enforces
      // the same cap defensively.
      params.set("limit", "30");
      const base = source === "assistant" ? `/silos/${siloId}/assistant-sessions` : `/silos/${siloId}/public-sessions`;
      const r = await api.get(`${base}?${params.toString()}`);
      setSessions(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load conversations.");
    } finally { setLoading(false); }
  }, [siloId, memberFilter, minMessages, search, ownerView, source]);

  // Reload whenever filters change (debounced on search input below)
  useEffect(() => {
    const t = setTimeout(load, search ? 350 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  const openSession = async (s) => {
    setSelectedSession(s);
    setSessionDetail(null);
    setDetailLoading(true);
    try {
      const base = source === "assistant" ? `/silos/${siloId}/assistant-sessions` : `/silos/${siloId}/public-sessions`;
      const r = await api.get(`${base}/${encodeURIComponent(s.session_id)}`);
      setSessionDetail(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load session.");
    } finally { setDetailLoading(false); }
  };

  const openInPublicQA = (s) => {
    if (!siloSlug) return;
    // PublicSilo will use ?session=<id> to pre-load the conversation. Same-tab
    // nav (was window.open _blank) so the creator's login session carries over —
    // new tabs start signed-out under the per-tab sessionStorage model.
    nav(`/s/${siloSlug}?session=${encodeURIComponent(s.session_id)}`);
  };

  const stats = useMemo(() => {
    const byLevel = { anonymous: 0, authenticated: 0, subscriber: 0, creator: 0, super_admin: 0 };
    let totalTurns = 0;
    let totalAudio = 0;
    let lastActive = null;
    for (const s of sessions) {
      byLevel[s.member_level] = (byLevel[s.member_level] || 0) + 1;
      totalTurns += s.message_count || 0;
      totalAudio += s.with_audio || 0;
      if (s.updated_at && (!lastActive || new Date(s.updated_at) > new Date(lastActive))) lastActive = s.updated_at;
    }
    return { total: sessions.length, totalTurns, totalAudio, lastActive, byLevel };
  }, [sessions]);

  return (
    <div className="space-y-6" data-testid="conversations-tab">
      {/* Header */}
      <div>
        <h2 className="font-serif-display text-3xl text-[#1C1B1A] flex items-center gap-2">
          <ChatsCircle size={24} weight="duotone" className="text-[#4A5D4E]" />
          Recent Conversations
        </h2>
        <p className="text-sm text-[#6B6A68] mt-1 max-w-2xl">
          {source === "assistant"
            ? "Your private AI Assistant conversations on this silo over the last 30 days — your own brainstorming and drafting sessions."
            : "Every conversation held on this silo's public Q&A page over the last 30 days. Use these to spot trending questions, identify your most engaged fans, and commit the best exchanges back into your library."}
        </p>
      </div>

      {/* Feb 2026 (Randy pt-yt-chevron) — chevron-collapsible wrapper around
          the Public Q&A / AI Assistant section so the YT Replies section
          below can feel like it sits right at the top of the tab. Uses a
          button + conditional rendering (not <details>) so the chevron
          animation and localStorage persistence stay under our control. */}
      <div ref={conversationsRef}>
        <button
          type="button"
          onClick={() => withConversationsAnchor(() => setConversationsOpen((v) => !v))}
          data-testid="conversations-section-toggle"
          className="w-full flex items-center gap-2 text-left px-2 py-1.5 rounded-md hover:bg-black/[0.02] transition-colors"
          aria-expanded={conversationsOpen}
          aria-controls="conversations-section-body"
        >
          <CaretDown
            size={14}
            weight="bold"
            className={`text-[#6B6A68] transition-transform ${conversationsOpen ? "rotate-0" : "-rotate-90"}`}
          />
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#6B6A68] flex-1 silo-loose">
            {source === "assistant" ? "AI Assistant conversations" : "Public Q&A conversations"}
            <span className="ml-2 normal-case tracking-normal text-[#8A8A88] italic silo-loose">
              {conversationsOpen ? "" : `(collapsed)`}
            </span>
          </span>
        </button>

        {conversationsOpen && (
          <div id="conversations-section-body" className="mt-3 space-y-6">
      {/* Source toggle (Jun 2026, Randy): Public Q&A vs AI Assistant — swaps the
          list AND the stat pills below. */}
      <div className="inline-flex rounded-lg border hairline bg-white p-1" data-testid="chats-source-toggle">
        <button onClick={() => setSource("public")} data-testid="chats-source-public"
          className={`px-4 py-1.5 text-sm rounded-md transition-colors ${source === "public" ? "bg-[#1C1B1A] text-[#FDFCF8]" : "text-[#6B6A68] hover:text-[#1C1B1A]"}`}>
          Public Q&A
        </button>
        <button onClick={() => setSource("assistant")} data-testid="chats-source-assistant"
          className={`px-4 py-1.5 text-sm rounded-md transition-colors ${source === "assistant" ? "bg-[#1C1B1A] text-[#FDFCF8]" : "text-[#6B6A68] hover:text-[#1C1B1A]"}`}>
          AI Assistant
        </button>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="conversations-stats">
        <StatCard label="Total sessions" value={stats.total} icon={ChatsCircle} />
        {source === "public" ? (
          <>
            <StatCard label="Anonymous"     value={stats.byLevel.anonymous || 0} icon={UserCircle} />
            <StatCard label="Signed-in"     value={stats.byLevel.authenticated || 0} icon={UserCircle} />
            <StatCard label="Subscribers 💰" value={stats.byLevel.subscriber || 0} icon={Heart} />
            <StatCard label="Total turns"   value={stats.totalTurns} icon={Sparkle} />
          </>
        ) : (
          <>
            <StatCard label="Total turns"  value={stats.totalTurns} icon={Sparkle} />
            <StatCard label="With audio"   value={stats.totalAudio} icon={SpeakerHigh} />
            <StatCard label="Last active"  value={stats.lastActive ? relativeTime(stats.lastActive) : "—"} icon={Clock} />
          </>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white border hairline rounded-lg p-4 flex flex-wrap items-end gap-3" data-testid="conversations-filters">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs uppercase tracking-[0.18em] text-[#6B6A68] mb-1.5">Search</label>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6A68]" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search question text or AI reply…"
              data-testid="conversations-search"
              className="w-full bg-white border hairline rounded-md pl-9 pr-9 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]" />
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#6B6A68] hover:text-[#1C1B1A]">
                <X size={12} />
              </button>
            )}
          </div>
        </div>
        {source === "public" && (
        <div>
          <label className="block text-xs uppercase tracking-[0.18em] text-[#6B6A68] mb-1.5">Member level</label>
          <select value={memberFilter} onChange={(e) => setMemberFilter(e.target.value)}
            data-testid="conversations-filter-member"
            className="bg-white border hairline rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]">
            <option value="">All</option>
            <option value="anonymous">Anonymous</option>
            <option value="authenticated">Signed-in</option>
            <option value="subscriber">Subscribers (paid)</option>
            <option value="creator">Creators</option>
            <option value="super_admin">Super Admin</option>
          </select>
        </div>
        )}
        <div>
          <label className="block text-xs uppercase tracking-[0.18em] text-[#6B6A68] mb-1.5">Min messages</label>
          <FreeNumberInput min={0} max={50} step={1} value={minMessages}
            onChange={(v) => setMinMessages(v)}
            data-testid="conversations-filter-minmsg"
            className="w-20 bg-white border hairline rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#4A5D4E] tabular-nums" />
        </div>
        {source === "public" && (
        <div>
          <label className="block text-xs uppercase tracking-[0.18em] text-[#6B6A68] mb-1.5">Owner self-tests</label>
          <select value={ownerView} onChange={(e) => setOwnerView(e.target.value)}
            data-testid="conversations-filter-owner-view"
            className="bg-white border hairline rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]">
            <option value="all">Show all</option>
            <option value="hide_mine">Hide chats to my own silo</option>
            <option value="only_mine">Only chats to my silo</option>
          </select>
        </div>
        )}
        <div className="flex items-end gap-2">
          <button onClick={load} data-testid="conversations-refresh"
            className="px-4 py-2 rounded-md border hairline text-sm text-[#1C1B1A] hover:border-[#4A5D4E] hover:text-[#4A5D4E] flex items-center gap-1.5">
            <Funnel size={14} /> Refresh
          </button>
          {selectedIds.size > 0 ? (
            <button onClick={deleteSelected} data-testid="conversations-delete-selected"
              className="px-4 py-2 rounded-md border border-[#C0392B] text-sm text-white bg-[#C0392B] hover:bg-[#A93226] flex items-center gap-1.5">
              <Trash size={14} weight="fill" /> Delete ({selectedIds.size})
            </button>
          ) : (
            <button onClick={() => { setSelectMode((m) => !m); setSelectedIds(new Set()); }} data-testid="conversations-select-toggle"
              className={`px-4 py-2 rounded-md border text-sm flex items-center gap-1.5 ${selectMode ? "border-[#4A5D4E] text-[#4A5D4E] bg-[#EEF2EE]" : "hairline text-[#1C1B1A] hover:border-[#4A5D4E] hover:text-[#4A5D4E]"}`}>
              <CheckSquare size={14} /> {selectMode ? "Cancel" : "Select"}
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border hairline rounded-lg overflow-hidden" data-testid="conversations-table-wrap">
        {loading && <div className="p-6 text-sm text-[#6B6A68] text-center">Loading conversations…</div>}
        {!loading && sessions.length === 0 && (
          <div className="p-10 text-center">
            <ChatsCircle size={40} weight="duotone" className="mx-auto text-[#A89A7A]" />
            <p className="text-sm text-[#6B6A68] mt-3">No conversations yet.</p>
            <p className="text-xs text-[#6B6A68] mt-1">They&apos;ll appear here as fans ask questions on your public silo page.</p>
          </div>
        )}
        {!loading && sessions.length > 0 && (
          <div className="overflow-x-auto">
            {/* May 25, 2026 — fixed-height window so the first ~10 rows
                show and the rest scroll inside the table. Header is
                sticky inside the scroll container so it stays visible
                while the user scrolls through deeper history. */}
            <div className="max-h-[640px] overflow-y-auto" data-testid="conversations-table-scroll">
              <table className="w-full text-sm" data-testid="conversations-table">
                <thead className="bg-[#FAF8F2] text-xs uppercase tracking-[0.15em] text-[#6B6A68] sticky top-0 z-10">
                <tr>
                  {selectMode && <th className="px-3 py-3 w-8"></th>}
                  {source === "public" && <th className="px-4 py-3 text-left">Visitor</th>}
                  <th className="px-4 py-3 text-left">First question</th>
                  <th className="px-4 py-3 text-right">Turns</th>
                  <th className="px-4 py-3 text-right">W/audio</th>
                  {source === "public" && <th className="px-4 py-3 text-left">Role</th>}
                  <th className="px-4 py-3 text-left">When</th>
                  {source === "public" && <th className="px-4 py-3 text-left">Device</th>}
                  <th className="px-4 py-3 text-right">Audio EXP</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => {
                  const badge = MEMBER_BADGES[s.member_level] || MEMBER_BADGES.anonymous;
                  const rid = s.bookmark_id || s.session_id.slice(0, 8);
                  const selectable = isSelectable(s);
                  return (
                    <tr key={s.session_id}
                      onClick={() => { if (selectMode) { if (selectable) toggleSel(s.session_id); } else openSession(s); }}
                      data-testid={`conversation-row-${rid}`}
                      className="border-t hairline cursor-pointer hover:bg-[#FAF8F2] transition-colors">
                      {selectMode && (
                        <td className="px-3 py-3" onClick={(e) => { e.stopPropagation(); if (selectable) toggleSel(s.session_id); }}>
                          {selectable ? (
                            selectedIds.has(s.session_id)
                              ? <CheckSquare size={18} weight="fill" className="text-[#4A5D4E]" data-testid={`conversation-checkbox-${rid}`} />
                              : <Square size={18} className="text-[#9b9a96]" data-testid={`conversation-checkbox-${rid}`} />
                          ) : (
                            <span className="text-[10px] text-[#c9c7c1]" title="A visitor's conversation can't be deleted from here.">—</span>
                          )}
                        </td>
                      )}
                      {source === "public" && (
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className="w-7 h-7 rounded-full bg-[#F0F1EE] flex items-center justify-center text-xs font-semibold tabular-nums" title={s.visitor_sig}>
                            {s.visitor_sig?.slice(0, 2).toUpperCase() || "??"}
                          </span>
                          <div className="min-w-0">
                            <span className="inline-flex text-[10px] px-1.5 py-0.5 rounded font-medium" style={{ background: badge.bg, color: badge.fg }}>
                              {badge.label}
                            </span>
                            {s.fan_email && (
                              <div className="text-[11px] text-[#6B6A68] truncate max-w-[160px]" title={s.fan_email}>
                                {s.fan_email}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      )}
                      <td className="px-4 py-3 max-w-md">
                        <div className="truncate text-[#1C1B1A] flex items-center gap-1.5" title={s.saved_title || s.first_question}>
                          {s.saved_title && <BookmarkSimple size={13} weight="fill" className="text-[#B0892F] shrink-0" />}
                          <span className="truncate">{s.saved_title || s.first_question || <em className="text-[#6B6A68]">No question yet</em>}</span>
                          {s.is_bookmark && (
                            <span className="inline-flex shrink-0 text-[9px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wide bg-[#FBF1DC] text-[#8A6A1C]"
                              title="Saved bookmark — the live conversation was cleared, but you saved this one."
                              data-testid={`conversation-saved-badge-${rid}`}>
                              Saved
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">{s.message_count}</td>
                      <td className="px-4 py-3 text-right tabular-nums" data-testid={`conversation-audio-${rid}`}>{s.with_audio ?? 0}</td>
                      {source === "public" && (
                      <td className="px-4 py-3">
                        {(() => {
                          const role = s.visitor_role || (s.fan_user_id ? "fan" : "anonymous");
                          const rb = VISITOR_ROLE_BADGES[role] || VISITOR_ROLE_BADGES.anonymous;
                          return (
                            <span className="inline-flex text-[10px] px-1.5 py-0.5 rounded font-medium whitespace-nowrap"
                              style={{ background: rb.bg, color: rb.fg }}
                              data-testid={`conversation-role-${rid}`}>
                              {rb.label}
                            </span>
                          );
                        })()}
                      </td>
                      )}
                      <td className="px-4 py-3 text-[#6B6A68] whitespace-nowrap" title={fmtDate(s.updated_at)}>{relativeTime(s.updated_at)}</td>
                      {source === "public" && (
                      <td className="px-4 py-3 text-[#6B6A68]">
                        {s.device === "mobile" ? <DeviceMobile size={14} weight="duotone" /> : <Desktop size={14} weight="duotone" />}
                        {s.country && <span className="ml-1 text-[11px]">{s.country}</span>}
                      </td>
                      )}
                      <td className="px-4 py-3 text-right tabular-nums text-[#6B6A68] whitespace-nowrap" data-testid={`conversation-audioexp-${rid}`}>
                        {s.audio_exp_hours != null ? `${s.audio_exp_hours}h` : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </div>

      {/* Side-panel: full session detail */}
      {selectedSession && (
        <SessionDetailDrawer
          session={selectedSession}
          detail={sessionDetail}
          loading={detailLoading}
          siloSlug={siloSlug}
          onClose={() => { setSelectedSession(null); setSessionDetail(null); }}
          onOpenInPublicQA={() => openInPublicQA(selectedSession)}
          onOpenInAssistant={() => nav(`/silo/${siloSlug}/assistant?session=${encodeURIComponent(selectedSession.session_id)}`)}
          isAssistant={source === "assistant"}
          onCommit={(messages) => scheduleOpenPicker(setPicker, "commit", messages)}
          onSendStaging={(messages) => scheduleOpenPicker(setPicker, "staging", messages)}
        />
      )}

      <SiloPickerModal
        open={picker.open}
        mode={picker.mode}
        messages={picker.messages}
        defaultTitle={picker.messages?.[0]?.text?.split(/\s+/).slice(0, 7).join(" ") || ""}
        onClose={() => setPicker({ open: false, mode: picker.mode, messages: [] })}
        onSuccess={() => setPicker({ open: false, mode: picker.mode, messages: [] })}
      />
          </div>
        )}
      </div>

      {/* Oct 2026 (Randy) — the YouTube Replies console moved OUT of this tab
          and into its own "YT Digital Twin" tab (components/YtTwinTab.jsx).
          Chats is now purely the audience-conversation surface, which is why
          it stays unlocked for every paying Creator. */}
    </div>
  );
}

// iter-25 fix: closing the commit modal left `picker.mode` as "commit" — clicking
// the staging button after a commit-modal-Escape then re-opened with mode="commit"
// because React batches the state updates. We set `open:false` first, then
// schedule the next open in a microtask so the child modal fully unmounts.
const scheduleOpenPicker = (setter, mode, messages) => {
  setter({ open: false, mode, messages: [] });
  // Force a tick so the modal's AnimatePresence exit fires before the new one enters.
  queueMicrotask(() => setter({ open: true, mode, messages }));
};

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="bg-white border hairline rounded-lg p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">
        <Icon size={12} weight="duotone" /> {label}
      </div>
      <div className="text-2xl font-semibold text-[#1C1B1A] tabular-nums mt-1">{value}</div>
    </div>
  );
}

function SessionDetailDrawer({ session, detail, loading, siloSlug, onClose, onOpenInPublicQA, onOpenInAssistant, onCommit, onSendStaging, isAssistant }) {
  const messages = detail?.messages || [];
  const msgObjects = messages.map((m) => ({ role: m.role === "user" ? "user" : "ai", text: m.text || "" }));
  const badge = MEMBER_BADGES[session.member_level] || MEMBER_BADGES.anonymous;

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex justify-end" data-testid="session-detail-drawer" onClick={onClose}>
      <div className="w-full max-w-2xl bg-white shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b hairline flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              {isAssistant ? (
                <span className="inline-flex text-[10px] px-2 py-0.5 rounded font-medium" style={{ background: "#EAF1EC", color: "#2F6B3A" }}>
                  AI Assistant
                </span>
              ) : (
                <>
                  <span className="inline-flex text-[10px] px-2 py-0.5 rounded font-medium" style={{ background: badge.bg, color: badge.fg }}>
                    {badge.label}
                  </span>
                  <span className="text-xs text-[#6B6A68]">Visitor {session.visitor_sig}</span>
                </>
              )}
            </div>
            <h3 className="font-serif-display text-2xl text-[#1C1B1A] truncate">{session.first_question || "Conversation"}</h3>
            <div className="text-[11px] text-[#6B6A68] mt-1 flex flex-wrap gap-3">
              <span><Clock size={10} weight="duotone" className="inline mb-0.5" /> {fmtDate(session.updated_at)}</span>
              {session.device && <span>{session.device === "mobile" ? "📱 mobile" : "🖥 desktop"}</span>}
              {session.country && <span><Globe size={10} className="inline mb-0.5" /> {session.country}</span>}
              {session.total_paid_cents > 0 && <span className="text-[#2F6B3A] font-medium"><CurrencyDollarSimple size={10} className="inline mb-0.5" /> {fmt$(session.total_paid_cents)} lifetime</span>}
              {session.member_since && <span>member since {new Date(session.member_since).toLocaleDateString()}</span>}
              {session.fan_email && <span><EnvelopeSimple size={10} className="inline mb-0.5" /> {session.fan_email}</span>}
              {session.referrer && <span className="truncate max-w-[240px]" title={session.referrer}>← {session.referrer.replace(/^https?:\/\//, "").split("/")[0]}</span>}
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-md hover:bg-[#F0F1EE]" data-testid="session-detail-close">
            <X size={16} />
          </button>
        </div>

        {/* Action toolbar */}
        <div className="px-6 py-3 border-b hairline flex flex-wrap gap-2 bg-[#FAF8F2]" data-testid="session-detail-actions">
          {isAssistant ? (
          <button onClick={onOpenInAssistant} data-testid="session-open-assistant"
            className="text-xs px-3 py-1.5 rounded-md border hairline bg-white text-[#1C1B1A] hover:border-[#4A5D4E] hover:text-[#4A5D4E] flex items-center gap-1.5">
            <ArrowSquareOut size={12} /> Open in AI Assistant
          </button>
          ) : (
          <button onClick={onOpenInPublicQA} data-testid="session-open-public"
            className="text-xs px-3 py-1.5 rounded-md border hairline bg-white text-[#1C1B1A] hover:border-[#4A5D4E] hover:text-[#4A5D4E] flex items-center gap-1.5">
            <ArrowSquareOut size={12} /> Open in public Q&A
          </button>
          )}
          <button onClick={() => onCommit(msgObjects)} disabled={!messages.length}
            data-testid="session-commit"
            className="text-xs px-3 py-1.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] flex items-center gap-1.5 disabled:opacity-50">
            <BookOpenText size={12} weight="duotone" /> Commit to silo
          </button>
          <button onClick={() => onSendStaging(msgObjects)} disabled={!messages.length}
            data-testid="session-staging"
            className="text-xs px-3 py-1.5 rounded-md bg-[#4A5D4E] text-[#FDFCF8] hover:bg-[#3A4A3E] flex items-center gap-1.5 disabled:opacity-50">
            <Stack size={12} weight="duotone" /> Send to staging
          </button>
        </div>

        {/* Transcript */}
        <div className="flex-1 overflow-y-auto p-6 space-y-3" data-testid="session-detail-transcript">
          {loading && <div className="text-sm text-[#6B6A68] text-center py-6">Loading transcript…</div>}
          {!loading && messages.map((m, i) => (
            <div key={i} className={`max-w-[90%] rounded-lg p-3 ${m.role === "user" ? "bg-[#F0F1EE] ml-auto" : "bg-white border hairline"}`}>
              <div className="text-[10px] uppercase tracking-[0.15em] text-[#6B6A68] mb-1">{m.role === "user" ? "Visitor" : "AI"}</div>
              <div className="text-sm text-[#1C1B1A] whitespace-pre-wrap leading-relaxed">{m.text}</div>
            </div>
          ))}
          {!loading && messages.length === 0 && (
            <p className="text-sm text-[#6B6A68] text-center py-6">This session has no messages yet.</p>
          )}
        </div>
      </div>
    </div>
  );
}
